import { useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { PASSWORD_MAX_BYTES, passwordByteLength } from '@qms360/shared';
import { ApiError, changePassword, fetchMe } from '../lib/api.js';
import { landingPath } from '../lib/landing.js';
import { useSession } from '../lib/session.js';
import { t } from '../lib/i18n.js';

/**
 * Change your own password — local accounts only (spec §2: dev and UAT sign-in).
 *
 * Two ways here, one screen:
 *
 *  - **Forced**, after an administrator reset. The API refuses everything else
 *    until this is done, so the session guard sends the user here and the page
 *    has nowhere to navigate to but back into the app once it succeeds.
 *  - **Chosen**, from the top bar.
 *
 * The rules are the server's. The minimum comes from master data through /me, so
 * the form can say it up front; the checks here only save a round trip, and the
 * API checks again either way.
 */
export function ChangePassword() {
  const { user, refresh, signOut } = useSession();
  const navigate = useNavigate();
  const me = useQuery({ queryKey: ['me'], queryFn: fetchMe });

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const forced = user?.passwordChangeRequired ?? false;
  const minLength = me.data?.passwordPolicy.minLength;

  if (user && user.authSource !== 'local') {
    return (
      <Shell title={t("Change password")}>
        <p className="m-0 text-sm text-content-secondary">
          {t("Your account signs in through the Algihaz platform, so it has no QMS360 password to change.")}
        </p>
      </Shell>
    );
  }

  const localProblem = (): string | null => {
    if (next !== confirm) return 'The new password and its confirmation do not match.';
    if (minLength !== undefined && [...next].length < minLength) {
      return `Choose a password of at least ${minLength} characters.`;
    }
    if (passwordByteLength(next) > PASSWORD_MAX_BYTES) {
      return `A password can be at most ${PASSWORD_MAX_BYTES} bytes long.`;
    }
    return null;
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const problem = localProblem();
    if (problem) {
      setError(problem);
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await changePassword(current, next);
      // Re-read the session rather than patching it: the server has just
      // cleared the flag, and the session context is a cache of that decision.
      await refresh();
      const me = await fetchMe().catch(() => null);
      navigate(landingPath(me?.capabilities), { replace: true });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t("Your password could not be changed."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell title={forced ? t("Choose a new password") : t("Change password")}>
      {forced && (
        <p className="m-0 rounded-sm bg-tone-pending-surface px-3 py-2 text-sm text-content-secondary">
          {t("An administrator reset your password. Enter the temporary password you were given, then choose one only you know. Nothing else is available until you do.")}
        </p>
      )}

      <form onSubmit={onSubmit} className="mt-6 flex flex-col gap-4">
        <Field
          id="current-password"
          label={forced ? t("Temporary password") : t("Current password")}
          autoComplete="current-password"
          value={current}
          onChange={setCurrent}
        />
        <Field
          id="new-password"
          label={t("New password")}
          autoComplete="new-password"
          value={next}
          onChange={setNext}
          hint={minLength !== undefined ? t("At least {0} characters.", { 0: minLength }) : undefined}
        />
        <Field
          id="confirm-password"
          label={t("Confirm new password")}
          autoComplete="new-password"
          value={confirm}
          onChange={setConfirm}
        />

        {error && (
          <p role="alert" className="m-0 rounded-sm bg-tone-critical-surface px-3 py-2 text-sm text-tone-critical">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={busy}
          className="rounded-sm bg-ag-purple px-4 py-2.5 font-medium text-content-inverse disabled:opacity-60"
        >
          {busy ? t("Saving…") : t("Change password")}
        </button>

        <button
          type="button"
          onClick={async () => {
            if (forced) {
              await signOut();
              navigate('/login', { replace: true });
            } else {
              navigate(-1);
            }
          }}
          className="text-sm text-content-secondary underline"
        >
          {forced ? t("Sign out instead") : t("Cancel")}
        </button>
      </form>
    </Shell>
  );
}

function Shell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <main className="mx-auto flex min-h-full max-w-md flex-col justify-center px-4 py-12">
      <div className="rounded-lg border border-border bg-surface p-8 shadow-card">
        <h1 className="m-0 mb-4 text-xl font-semibold">{title}</h1>
        {children}
      </div>
    </main>
  );
}

function Field({
  id,
  label,
  autoComplete,
  value,
  onChange,
  hint,
}: {
  id: string;
  label: string;
  autoComplete: string;
  value: string;
  onChange: (value: string) => void;
  hint?: string;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium">
        {label}
      </label>
      <input
        id={id}
        type="password"
        autoComplete={autoComplete}
        required
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-describedby={hint ? `${id}-hint` : undefined}
        className="rounded-sm border border-border-strong px-3 py-2 text-content-primary"
      />
      {hint && (
        <p id={`${id}-hint`} className="m-0 text-xs text-content-secondary">
          {hint}
        </p>
      )}
    </div>
  );
}
